import EditIcon from "@mui/icons-material/Edit";
import { Box, CircularProgress, IconButton, Stack, SxProps, useTheme } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { noop } from "lodash";
import moment from "moment";
import { FC, useMemo, useState } from "react";
import { baseAtom } from "../../../stores/jotai/base";
import {
    setSelectedTemplate,
    templatesAtom,
} from "../../../stores/jotai/template/templates";
import { useDeleteTemplate } from "../../../tanstackQuery/queries/templates";
import MaterialTable from "../../general/material-table";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import DeleteIcon from "../../../../icons/DeleteIcon"
import { getDateFormat } from "../../../util";
import DeleteOutlinedIcon from "../../../../icons/DeleteOutlinedIcon";
//import DeleteIcon from "@mui/icons-material/Delete";

interface ITemplateList {
    searchValue?: string;
    readonly?: boolean;
    onSelectRow?: Function;
    isHome?: boolean;
    filteredTemplates?:any[];
    filteredTemplatesUsers?:any;
    templateTableFromHome?:boolean;
}

const TemplateList: FC<ITemplateList> = (props) => {
    const { searchValue, readonly, onSelectRow, isHome,filteredTemplates,filteredTemplatesUsers,templateTableFromHome } = props;
    const [templateIDBeingDeleted, setTemplateIDBeingDeleted] =
        useState<string>(null);
    const [base, setBase] = useAtom(baseAtom);
    const setTemplateAtomState = useSetAtom(templatesAtom);
    const { keycloak } = useKeycloak();
    const client = useQueryClient();
    const discoverySession = useAtomValue(discoverySessionAtom);
    const theme = useTheme()
    const teamID = base.team.groupID;
    const { mutateAsync: deleteTemplate } = useDeleteTemplate({accessToken: keycloak.token},{
        onSuccess() {
          client.invalidateQueries({ queryKey: ["templates"] });
        }}
    );

    const handleTemplateDeletion = (templateID) => {
        if (!readonly) {
            setTemplateIDBeingDeleted(templateID);

            deleteTemplate({ templateID: templateID, groupID: teamID, sessionID: discoverySession?.sessionID }).then(
              () => {
                setTemplateIDBeingDeleted(null);
              }
            );
        }
    };


    const tableData = useMemo(() => {
        filteredTemplates?.sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime())
        return filteredTemplates?.map((template) => ({
            name: template?.name,
            creator: filteredTemplatesUsers?.[template?.userID]?.name,
            modified: moment(template?.modified).format(getDateFormat()),
            ...(!readonly && {
                action: (
                    <Stack
                        className="TemplateListRowActionData"
                        display="inline-flex"
                        direction="row"
                        spacing={0}
                    >

                        {!isHome && (
                            <IconButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedTemplate(setTemplateAtomState, template);
                                }}>
                                <EditIcon />
                            </IconButton>)
                        }
                        
                        {template?.templateID === templateIDBeingDeleted ? (
                            <Box padding="8px">
                                <CircularProgress
                                    size={20}
                                    sx={{
                                        display: "block",
                                    }}
                                />
                            </Box>
                        ) : (
                            <IconButton
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleTemplateDeletion(template.templateID);
                                }}
                                sx={{
                                  ":hover": {
                                    color: `#1976D2 !important`,
                                    // padding:'4px 2px 4px 5px'
                                  }
                                }}
                                size="small"
                            >
                               {/* <IconButton sx={{  padding:'4px 2px 4px 5px' }} onClick={() => this.remove()}> */}
            <DeleteOutlinedIcon sx={{
              color: "gray",
              fill:"none",
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
            {/* </IconButton> */}
                            </IconButton>
                        )}
                    </Stack>
                ),
            }),
            id: template.templateID,
        }));
    }, [filteredTemplates, readonly, templateIDBeingDeleted]);

    const filteredData = useMemo(() => {
        if (!searchValue) {
            return tableData;
        }

        return tableData?.filter((data) => {
            if (data?.name?.toLowerCase().includes(searchValue)) {
                return true;
            }

            if (data?.creator?.toLowerCase().includes(searchValue)) {
                return true;
            }

            return false;
        });
    }, [tableData, searchValue]);

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
          {
            accessorKey: 'name', 
            header: 'Name',
            size: templateTableFromHome ? 200: 300,
          },
          {
            accessorKey: 'creator',
            header: 'Owner',
            size: templateTableFromHome ? 100: 200,
          },
          {
            accessorKey: 'modified', 
            header: 'Last modified',
            size: templateTableFromHome ? 140: 186,
          },
          {
            accessorKey: "action",
            header: "",
            id: "action", 
            size: templateTableFromHome ? 70: 100,
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ({ row }) => (<>{row.original.action}</>),
          },
        ],
        [],
      );
      const table = useMaterialReactTable({
        columns,
        data:filteredData || [], 
        enableHiding: false,
        enableDensityToggle: false,
        enablePagination: false,
        enableRowVirtualization: true,
        enableColumnActions: false,
        enableBottomToolbar: false,

        renderTopToolbar: ({ table }) => {
            return <div style={{ width: "0px", height: "0px",paddingTop:'10px' }}></div>;
        },
        muiTableBodyRowProps: ({ row }) => ({
            hover: false,
            onClick: () => {
                setSelectedTemplate(setTemplateAtomState, {
                  templateID: row.original.id,
                  name: row.original.name,
                });
                onSelectRow();
              },
              sx: {
                cursor: "pointer",
                backgroundColor:"transparent",
                "&:hover .TemplateListRowActionData .MuiIconButton-root": {
                    opacity: 1,
                    pointerEvents: "auto",
                  },
                ":hover": { backgroundColor: theme.custom.hoverOptionColor }
              },
          }),
          muiTableContainerProps: {
            sx: {
              maxHeight: `${templateTableFromHome ? "390px" : "calc(100vh - 335px)"}`,
            }
          },
          muiTableBodyCellProps: ({ column }) => ({
            sx: {
                height: "auto !important",
                py: '6px',
              ...(column.id === "action" && {
                "& .TemplateListRowActionData .MuiIconButton-root": {
                  opacity: 0, 
                  pointerEvents: "none",
                  transition: "opacity 0.2s ease-in-out",
                },
              }),
            },
          }),  
          muiTableHeadProps: {
            sx: {
              pt: "0px",
              borderBottom: `1px solid inherit !important`,
              boxShadow: "none !important",
              "& tr":{
                boxShadow: "none !important",
              }
            },
          },
      });
    return (
         <MaterialReactTable table={table} />
    );
};

TemplateList.defaultProps = {
    searchValue: "",
    readonly: false,
    onSelectRow: noop,
    templateTableFromHome: false,
};

export default TemplateList;
